import { constants } from '../../misc/constants';
import VideoEnvironment, { VideoPart } from '../Components/Environments/React/VideoEnvironment';
import { CoreLoadingAnimationLoadingType } from '../Components/ui/react/core-loading-animation';
import { LoadingUIConfig } from '../Components/ui/react/loading-ui';
import GameManager from '../GameManager';
import Tools from '../Misc/Tools';
import BaseScenarioPart from './BaseScenarioPart';
import { Segments } from './Segments';

/**
 * Base class for all scenarios
 */
export default class BaseScenario {
	/**
	 * Custom properties for the scenario
	 */
	 public props: any;
	 
	 public get gameManager(): GameManager {
		return this._gameManager;
	 }

	/**
	  * ctor
	  * @param _gameManager A reference to the game manager
	  * @param scenarioName The name, or id, of the scenario
	  */
	constructor(private _gameManager: GameManager, scenarioName: string) {
		this.props = { name: scenarioName };
	}

	public init(onDoneLoading: () => void, onDone: () => void): void {
		throw new Error('init must be implemented');
	}

	public shouldLoadScenario(): boolean {
		throw new Error('shouldLoadScenario must be implemented');
	}

	public blurFadeIn(): void {
		this._gameManager.environment.blurFadeIn();
	}

	public blurFadeOut(): void {
		this._gameManager.environment.blurFadeOut();
	}

	/**
	 * @returns Gets all the parts for the scenario
	 */
	public getParts(): BaseScenarioPart<any>[] {
		throw new Error('getParts must be implemented');
	}

	public getEnvironmentProps(): any {
		return null;
	}

	/**
	 * Loads a part
	 */
	public loadPart(partId: string, onDone?: () => void): void {
		let part: any = undefined;
		let parts = this.getParts();
		let onReady = () => {
			if(onDone) {
				onDone();
			}
			part.start();
		};
		for(let partIndx = 0; partIndx < parts.length; partIndx++) {
			part = parts[partIndx];
			if(part.partId !== partId) {
				continue;
			}

			let obj: any = {
				partId: part.partId,
				url: part.videoUrl,
				
			};
			// this.loadAllVideosInEnvironment(obj, onReady);
			// this.loadAllVideosInEnvironment(obj, () => {});
			onReady();
			return;
		}
	}

	/**
	 * Plays a video chapter
	 */
	public playVideoChapter(partId: string, chapterId: string, onDone: () => void, loop?: boolean): void {
		(this._gameManager.environment as VideoEnvironment).playSequence({
			sequenceName: 'video',
			props: {
				partId: partId,
				chapterId: chapterId,
				loop: loop
			},
			onDone: onDone
		})
	}

	 /**
	  * Starts the scenario
	  */
	 public start(): void {
		throw new Error('start must be implemented');
	}
 
	 /**
	  * Starts the scenario once it can be started
	  */
	 public startWhenReady(): void {
		 if(!this._gameManager || !this._gameManager.environment){
			 return;
		 }
		let context = this;
		const loadingUIID = this.gameManager.UiManager.createUIInstance(constants.uiNames.LoadingUI, {
			loadingContainerWidth: 300,
			loadingContainerHeight: 180,
			loadingZoomLvl: 2,
			loadingType: CoreLoadingAnimationLoadingType.four
		} as LoadingUIConfig)
		let onReadyToStart = () => {
			context.init(()=> {
				this.gameManager.UiManager.destroyUIInstanceById(loadingUIID);
				Tools.forceRerenderUI(constants.uiNames.LoadingUI);
			}, () => { context.start(); });
		}
		if(this._gameManager.environment.isReady) {
			onReadyToStart();
		 } else {
			 if(!this._gameManager.environment.props) {
			 	this._gameManager.environment.props = this.getEnvironmentProps() ?? this._gameManager.environment.getDefaultProps();
			 }
			 this._gameManager.environment.onReady = () => {
				onReadyToStart();
			 };
		 }
	 }
 
	 /**
	  * Ends the scenario
	  */
	 public endScenario(): void {
		 let url = new URL(window.location.href);
		 let search_params = url.searchParams;
		 
		 search_params.set('w', 'scenarios');
 
		 url.search = search_params.toString();
 
		 window.location.href = url.toString();
		 //window.location.href = process.env.PUBLIC_URL;
	 }

	 /**
	  * The total number of questions in a scenario
	  */
	public get totalNumberOfQuestions(): number {
		throw new Error('totalNumberOfQuestions must be implemented');
	}

	/**
	 * Core needs this to know the path to the audio file
	 * @returns The full path to the sound
	 */
	public getSoundPath(fileName: string): string {
		throw new Error('getSoundPath must be implemented');
	}

	/**
	 * Plays a sound
	 */
	public playSound(fileName: string, onDone?: () => void, oneShot?: boolean, muted?: boolean, useRawPath?: boolean): void {
		this._gameManager.SoundManager.stopAllSounds();
		let context = this;
		context._gameManager.SoundManager.playSound({
			src: useRawPath ? fileName : this.getSoundPath(fileName),
			onEnded: onDone,
			oneShot: typeof oneShot === 'undefined' ? true : oneShot,
			playsInline: true,
			autoPlay: true,
			enableClosedCaptioning: true,
			muted: muted
		});
	}

	/**
	 * plays a Unity3D visualizer sequence
	 */
	public playUnity3DSequence(name: string, props?: any, onDone?: () => void, onError?: () => void): void {
		this._gameManager.customData.dispatch({
			type: 'playSequence',
			value: {
				sequenceName: name,
				props: {
					onDone: onDone,
					onError: onError,
					sequenceProps: props
				}
			}
		});
	}

	/**
	 * Loads all segments for a specific part
	 * @param partSegments All segements belonging to a part
	 */
	 private loadSegments(partSegments: Segments): any {
		let videos = [];
		for(let segmentIndx = 0; segmentIndx < partSegments.segments.length; segmentIndx++) {
			let singleSegment = partSegments.segments[segmentIndx];
			videos.push({
				segmentId: singleSegment.segmentId,
				data: singleSegment.getVideo()
			});
		}

		return videos;
	}

	public loadAllVideosInEnvironment(obj: any, onLoaded?: () => void) {
		let props = this._gameManager.environment.props ?? this.getEnvironmentProps();

		if(!props) {
			props = this._gameManager.environment.getDefaultProps();
		}
		this._gameManager.environment.props = {...props, part: obj, isDirty: true, onPartsLoaded: onLoaded};
		(this._gameManager.environment.props as any).onForceUpdate();
	}
}